/**
 * ChoiceCard — the GroundTruth bulk editor's structured clarification question.
 *
 * The agent explains in one message and then asks through `present_choices`;
 * this card is that ask, rendered as its own message right under the
 * explanation instead of a question buried at the end of a paragraph. Users
 * asked for exactly this: a list of options quick to read and quick to pick.
 *
 *   · binary   — Yes / No, only for a genuine yes/no decision
 *   · numbered — 2–5 alternatives, each a number, a short label and a
 *                one-line description
 *
 * Every option is a real <button>, so it is reachable and operable from the
 * keyboard, and its accessible name spells out the number, label and
 * description. Nothing depends on colour: the number is text, a picked option
 * says "Selected", and a closed card says why in words.
 *
 * Only an OPEN card is clickable. Once it is answered, replaced by a newer
 * question, or expired with its session, it stays in the chat read-only — the
 * record of what was asked and what was picked.
 *
 * A pick is never an approval: `onChoose` answers the question, and the agent
 * turns the answer into a new preview the user still has to confirm.
 */

import { useEffect, useId, useRef } from 'react';
import { choiceTypingHint } from './choiceResolver';

// Read by GroundTruth account managers: plain words, no dashes (the same rules
// the agent's own text follows).
const CLOSED_TEXT = {
  superseded: 'This question was replaced. It is no longer active.',
  expired: 'This question is from an earlier session. It is no longer active.',
};

export function ChoiceCard({
  choiceSet,
  onChoose,
  // choice_set_id currently being sent, so the card can say so and refuse a
  // second click.
  busy = false,
  error = null,
  // Rendered again under a "that didn't pick an option" note.
  redisplay = false,
}) {
  const headingId = useId();
  const cardRef = useRef(null);
  const footRef = useRef(null);
  const wasOpen = useRef(false);
  const isOpen = choiceSet?.status === 'open' && typeof onChoose === 'function';
  // Answering disables every option — including the one that had focus, which
  // would drop keyboard focus to <body>. Hand it to the footer that now says
  // what was picked. Only when focus was inside this card.
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (!active || active === document.body || cardRef.current?.contains(active)) {
        footRef.current?.focus?.();
      }
    }
    wasOpen.current = isOpen;
  }, [isOpen]);
  if (!choiceSet) return null;

  const {
    choice_set_id: choiceSetId,
    kind,
    question,
    choices = [],
    status,
    resolution,
  } = choiceSet;
  const binary = kind === 'binary';
  const open = isOpen;
  const shown = choices.slice(0, 5);
  const picked = resolution ? shown.find((c) => c.id === resolution.choice_id) : null;

  let footer;
  if (open) footer = busy ? 'Sending your choice…' : choiceTypingHint({ ...choiceSet, choices: shown });
  else if (picked) footer = `You chose ${binary ? picked.label : `option ${picked.number}: ${picked.label}`}.`;
  else footer = CLOSED_TEXT[status] || 'This question is no longer active.';

  return (
    <div
      ref={cardRef}
      className={`clw-choice-card ${binary ? 'binary' : 'numbered'} ${open ? 'open' : `closed status-${status || 'closed'}`}${redisplay ? ' redisplay' : ''}`}
      role="group"
      aria-labelledby={headingId}
    >
      <div className="clw-choice-head">
        <span className="clw-choice-badge">{binary ? 'Yes or no' : 'Choose one'}</span>
        <strong id={headingId} className="clw-choice-question">{question}</strong>
      </div>
      <ul className="clw-choice-list">
        {shown.map((c) => {
          const isPicked = picked?.id === c.id;
          const mark = binary ? c.label : String(c.number);
          const name = `${binary ? '' : `Option ${c.number}: `}${c.label}`
            + `${c.description ? `. ${c.description}` : ''}${isPicked ? '. Selected' : ''}`;
          return (
            <li key={c.id} className="clw-choice-item">
              <button
                type="button"
                className={`clw-choice-option${isPicked ? ' picked' : ''}${c.role ? ` escape ${c.role}` : ''}`}
                disabled={!open || Boolean(busy)}
                aria-label={name}
                onClick={() => onChoose?.(choiceSetId, c.id)}
              >
                {!binary && <span className="clw-choice-num" aria-hidden="true">{mark}</span>}
                <span className="clw-choice-body">
                  {/* "Selected" shares the label's line, so a picked option's
                      description wraps at the same width as the others and
                      side-by-side Yes / No boxes stay the same size. */}
                  <span className="clw-choice-label-row">
                    <span className="clw-choice-label">{c.label}</span>
                    {isPicked && <span className="clw-choice-picked" aria-hidden="true">✓ Selected</span>}
                  </span>
                  {c.description && <span className="clw-choice-desc">{c.description}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {/* Always a live region, so the change from the hint to "You chose…" /
          "replaced" is announced rather than inserted silently. */}
      <div className="clw-choice-foot" role="status" tabIndex={-1} ref={footRef}>{footer}</div>
      {open && error && <div className="clw-bulk-error" role="alert">{error}</div>}
    </div>
  );
}

export default ChoiceCard;
